import i18n from "@/plugins/i18n";
import { XmlDocumentWrapper } from "@/utilities/xml-helpers/xml-document-wrapper";
import { XmlElementWrapper } from "@/utilities/xml-helpers/xml-element-wrapper";
import axios, { AxiosRequestConfig, Method } from "axios";
import { RequestModelInterface } from "../models/request-model-interface";
import { ResponseModelInterface } from "../models/response-model-interface";

export abstract class BaseRequest
{
    /**
     * A cancelation object that will be passed to the request instance and can be used to
     * cancel it.
     */
    protected cancelSource = axios.CancelToken.source();

    /**
     * Provide the HTTP method to use for the request.
     */
    protected abstract getMethod(): Method;

    /**
     * Provide the URI to be used for this request.
     */
    protected abstract getUri(): string;

    /**
     * A method that will be called when the response is received and that has to
     * return a ResponseModel from the XML response.
     * 
     * @param elem          The root node of the XML response received from the server
     */
    protected abstract populateResponse(elem: XmlElementWrapper): ResponseModelInterface;

    /**
     * Can optionnaly be overriden to provide data for the request body.
     * 
     * @returns             A serializable request model, or null if there should be no body for this request
     */
    protected getData(): RequestModelInterface|null
    {
        return null;
    }

    /**
     * Send the request and returns a promise that will resolve when the response is received
     * and decoded.
     * 
     * @returns             A promise resolved when the response is available
     */
    public async load(): Promise<ResponseModelInterface>
    {
        const client = axios.create({
            baseURL: process.env.VUE_APP_API_BASE_URL,
            cancelToken: this.cancelSource.token,
        });

        const options: AxiosRequestConfig = {
            url: this.getUri(),
            method: this.getMethod(),
        };

        const data = this.getData();
        if (data !== null) {
            const xmlData = data.getXml(XmlDocumentWrapper.instance());
            const serializer = new XMLSerializer();

            options.data = serializer.serializeToString(xmlData.element);
            options.headers = {
                'Content-Type': 'application/xml',
            };
        }

        const response = await client.request(options);

        const parser = new DOMParser();
        const responseDom = parser.parseFromString(response.data, "application/xml");
        const rootXmlElement = new XmlElementWrapper(
            responseDom.documentElement,
            i18n.global.locale.value,
            "" //i18n.global.fallbackLocale.value
        );

        return this.populateResponse(rootXmlElement);
    }

    /**
     * Cancel the query if it is running. Caution, this is a HTTP level cancelation and must
     * not be confused with a potential order to the server to cancel a running request.
     * When using this method, the browser will stop listening for the response and may eventually
     * decide to close the socket with the server. But the behavior of the server is unknown; it should
     * be considered that it will continue to process the request and will only notice the absence
     * of the client when it tries to send the response.
     */
    public cancel(): void
    {
        this.cancelSource.cancel();
    }
}
